/**
 * Vinheria — Busca Preditiva
 * Mostra sugestões enquanto o usuário digita, usando o endpoint
 * predictive_search do Shopify: /search/suggest.json
 */
(function() {
  'use strict';

  const DEBOUNCE_MS = 250;
  const MIN_CHARS = 2;

  let debounceTimer = null;
  let lastQuery = '';

  /* ============================================================
     1. INPUT HANDLER (debounce)
     ============================================================ */
  document.addEventListener('input', function(e) {
    const input = e.target;
    if (!input.matches('[data-vin-predictive-input]')) return;

    const query = input.value.trim();
    const wrapper = input.closest('[data-vin-predictive-wrapper]');
    if (!wrapper) return;

    // Botão "X" para limpar
    const clearBtn = wrapper.querySelector('[data-vin-search-clear]');
    if (clearBtn) {
      clearBtn.classList.toggle('visible', query.length > 0);
    }

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      hidePredictive(wrapper);
      return;
    }

    if (query === lastQuery) return;
    lastQuery = query;

    debounceTimer = setTimeout(() => {
      fetchPredictive(query, wrapper);
    }, DEBOUNCE_MS);
  });

  /* ============================================================
     2. CLEAR BUTTON
     ============================================================ */
  document.addEventListener('click', function(e) {
    const clearBtn = e.target.closest('[data-vin-search-clear]');
    if (!clearBtn) return;
    e.preventDefault();
    const wrapper = clearBtn.closest('[data-vin-predictive-wrapper]');
    if (!wrapper) return;
    const input = wrapper.querySelector('[data-vin-predictive-input]');
    if (input) {
      input.value = '';
      input.focus();
      hidePredictive(wrapper);
      clearBtn.classList.remove('visible');
    }
  });

  /* ============================================================
     3. FETCH SUGESTÕES (Shopify Predictive Search API)
     ============================================================ */
  function fetchPredictive(query, wrapper) {
    const panel = getPanel(wrapper);
    if (!panel) return;

    panel.innerHTML = '<div class="vin-predictive-loading">Buscando…</div>';
    panel.classList.add('visible');

    const url = window.shopUrls.predictive_search_url +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection' +
      '&resources[limit]=6' +
      '&section_id=vinheria-predictive';

    // Tenta buscar via JSON (mais leve)
    fetch('/search/suggest.json?q=' + encodeURIComponent(query) +
          '&resources[type]=product,collection&resources[limit]=6')
      .then(r => r.json())
      .then(data => {
        renderPredictive(data, query, panel);
      })
      .catch(() => {
        panel.innerHTML = '<div class="vin-predictive-empty">Erro ao buscar. Tente novamente.</div>';
      });
  }

  /* ============================================================
     4. RENDERIZAR RESULTADOS
     ============================================================ */
  function renderPredictive(data, query, panel) {
    const products = (data.resources && data.resources.results && data.resources.results.products) || [];
    const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];

    if (products.length === 0 && collections.length === 0) {
      panel.innerHTML = `
        <div class="vin-predictive-empty">
          <div style="font-size: 40px; margin-bottom: 10px; opacity: 0.4;">🍷</div>
          Nenhum vinho encontrado para "<strong>${escapeHtml(query)}</strong>"
        </div>
      `;
      return;
    }

    let html = '';

    // Coleções
    if (collections.length > 0) {
      html += '<div class="vin-predictive-section">';
      html += '<div class="vin-predictive-section-title">Categorias</div>';
      collections.forEach(col => {
        html += `
          <a href="${escapeAttr(col.url)}" class="vin-predictive-item">
            <div class="vin-predictive-img" style="background: linear-gradient(135deg, var(--burgundy), var(--burgundy-deep)); color: var(--gold); font-size: 22px; align-items: center; justify-content: center;">🍷</div>
            <div class="vin-predictive-info">
              <div class="vin-predictive-name">${escapeHtml(col.title)}</div>
              <div class="vin-predictive-meta">Coleção</div>
            </div>
          </a>
        `;
      });
      html += '</div>';
    }

    // Produtos
    if (products.length > 0) {
      html += '<div class="vin-predictive-section">';
      html += '<div class="vin-predictive-section-title">Vinhos</div>';
      products.forEach(prod => {
        const img = prod.image ?
          `<img src="${escapeAttr(prod.image)}" alt="">` :
          '<span style="font-size: 24px; opacity: 0.5;">🍷</span>';
        const price = prod.price ? formatMoney(prod.price) : '';
        html += `
          <a href="${escapeAttr(prod.url)}" class="vin-predictive-item">
            <div class="vin-predictive-img">${img}</div>
            <div class="vin-predictive-info">
              <div class="vin-predictive-name">${escapeHtml(prod.title)}</div>
              ${prod.vendor ? `<div class="vin-predictive-meta">${escapeHtml(prod.vendor)}</div>` : ''}
            </div>
            ${price ? `<div class="vin-predictive-price">${price}</div>` : ''}
          </a>
        `;
      });
      html += '</div>';
    }

    // Link "Ver todos os resultados"
    html += `
      <a href="/search?q=${encodeURIComponent(query)}" class="vin-predictive-see-all">
        Ver todos os resultados para "${escapeHtml(query)}" →
      </a>
    `;

    panel.innerHTML = html;
  }

  /* ============================================================
     5. HELPERS
     ============================================================ */
  function getPanel(wrapper) {
    return wrapper.querySelector('[data-vin-predictive-panel]');
  }

  function hidePredictive(wrapper) {
    const panel = getPanel(wrapper);
    if (panel) {
      panel.classList.remove('visible');
      panel.innerHTML = '';
    }
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  function formatMoney(cents) {
    // Shopify retorna preço em centavos
    if (typeof cents === 'string') {
      // Já formatado, retornar como está
      return cents;
    }
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return 'R$ ' + value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /* ============================================================
     6. ESCONDER QUANDO CLICAR FORA
     ============================================================ */
  document.addEventListener('click', function(e) {
    const wrappers = document.querySelectorAll('[data-vin-predictive-wrapper]');
    wrappers.forEach(w => {
      if (!w.contains(e.target)) {
        hidePredictive(w);
      }
    });
  });

  /* ============================================================
     7. ESC PARA FECHAR
     ============================================================ */
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      const wrappers = document.querySelectorAll('[data-vin-predictive-wrapper]');
      wrappers.forEach(w => hidePredictive(w));
    }
  });

})();